import React from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, ArrowRight, X } from "lucide-react";
import { useCart } from "../context/CartContext";

/**
 * Sticky bottom bar that appears on the finder-results page whenever the
 * cart has items. Gives the buyer instant visual confirmation after each
 * quick-add plus a one-click path to the cart — no need to hunt for the
 * Cart icon in the header.
 *
 * Visible:
 *  - Only on /products with a technical filter (DN / mm / inch)
 *  - Only when count > 0
 *  - Hidden after manual dismiss (state in localStorage so it stays away
 *    until next session)
 */
export default function FinderCartBar() {
  const { count, subtotal } = useCart();
  const [dismissed, setDismissed] = React.useState(false);

  if (count === 0 || dismissed) return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-30 flex justify-center px-4 pb-4"
      data-testid="finder-cart-bar"
    >
      <div className="pointer-events-auto flex w-full max-w-2xl items-center gap-3 rounded-full border-2 border-nibro-sage bg-white px-4 py-3 shadow-2xl shadow-zinc-900/15 sm:gap-4 sm:px-6">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
          <ShoppingCart size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span
              className="font-display text-sm font-bold text-zinc-900 sm:text-base"
              data-testid="finder-cart-bar-count"
            >
              {count} item{count === 1 ? "" : "s"} toegevoegd
            </span>
            <span className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">
              · €{subtotal.toFixed(2)} totaal
            </span>
          </div>
        </div>
        <Link
          to="/cart"
          data-testid="finder-cart-bar-checkout"
          className="inline-flex flex-shrink-0 items-center gap-2 rounded-full bg-nibro-sage px-4 py-2 text-xs font-bold uppercase tracking-wider text-white transition hover:bg-nibro-sage-hover sm:text-sm"
        >
          Naar besteloverzicht <ArrowRight size={14} />
        </Link>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Sluiten"
          data-testid="finder-cart-bar-dismiss"
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-zinc-400 transition hover:bg-zinc-100 hover:text-zinc-700"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
